/**
 * LiveTripMap — crew pickup / drop live tracking on a Leaflet map.
 *
 * Presentational: it does not fetch. Hand it the two calls your app owns
 * (src/apis/creweasyMobileApi.js) and it drives them:
 *
 *   <LiveTripMap fetchers={{
 *     getTracking: () => getTripTracking(params),        // get-trip-tracking
 *     getGps: () => getVehicleGpsLocation(params),       // get-vehice-gps-loaction
 *     gpsIntervalMs: 10000,
 *   }} />
 *
 * Memoise that object (see TripTrackingMap.jsx) or polling restarts on every
 * parent render. Or skip fetching entirely and pass a payload:
 *
 *   <LiveTripMap tracking={response.payload} />
 *
 * Payload use:
 *   driverInfo          → driver card (photo, name, call, vehicleNo, vehicleType)
 *   passengers          → crew list; isCurrentPassenger drives header and target
 *   destination         → drop pane, typed geofence, drop marker
 *   eta                 → trackingMessage, distanceValue (current leg, m),
 *                         durationValue, routeDistanceValue (whole trip) → half dial
 *   route.mapJson       → the drawn route, alternates and turn list
 *   vehicelGps          → vehicle marker, heading, speed, altitude, gpsTime
 *
 * getTracking is re-called when a GPS fix lands more than OFF_ROUTE_M off the
 * plotted route on two consecutive polls, so a diverted driver self-corrects.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  routeFromPayload, measure, snapToRoute, bearing, km, minutesOf, metres,
  gpsFrom, currentPassenger, connectTrip,
} from './tripRoute';

const TILE = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const ACCENT = '#3ee0a3';

// Destination type drives the pin glyph and geofence radius.
const GLYPH = {
  airport: 'M21 16v-2l-8-3V4.5a1.5 1.5 0 0 0-3 0V11l-8 3v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5L21 16z',
  hotel: 'M4 6h2v5h12a3 3 0 0 1 3 3v5h-2v-3H6v3H4V6zm4.5 1a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5z',
  home: 'M12 3 3 10h2v10h6v-6h2v6h6V10h2L12 3z',
  duty: 'M9 4h6a2 2 0 0 1 2 2v1h3a1 1 0 0 1 1 1v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a1 1 0 0 1 1-1h3V6a2 2 0 0 1 2-2zm0 3h6V6H9v1z',
  pin: 'M12 2a7 7 0 0 0-7 7c0 5.2 7 13 7 13s7-7.8 7-13a7 7 0 0 0-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z',
};

export function placeMeta(dest, text) {
  if (dest?.isAirport) return { kind: 'Airport', glyph: GLYPH.airport, outer: 700, inner: 320 };
  if (dest?.isHotel) return { kind: 'Hotel', glyph: GLYPH.hotel, outer: 300, inner: 140 };
  if (dest?.isResidence) return { kind: 'Residence', glyph: GLYPH.home, outer: 180, inner: 80 };
  if (dest?.isGroundDuty) return { kind: 'Ground duty', glyph: GLYPH.duty, outer: 350, inner: 160 };
  if (/airport|terminal/i.test(text || '')) return { kind: 'Airport', glyph: GLYPH.airport, outer: 700, inner: 320 };
  if (/hotel|resort/i.test(text || '')) return { kind: 'Hotel', glyph: GLYPH.hotel, outer: 300, inner: 140 };
  return { kind: 'Pickup point', glyph: GLYPH.pin, outer: 260, inner: 120 };
}

const vehicleIcon = () => L.divIcon({
  className: '', iconSize: [30, 30], iconAnchor: [15, 15],
  html: `<div class="ltm-veh"><i><svg viewBox="0 0 24 24" fill="#07150f">
    <path d="M12 2 21 21l-9-5-9 5z"/></svg></i></div>`,
});
const targetIcon = (meta) => L.divIcon({
  className: '', iconSize: [34, 42], iconAnchor: [17, 40],
  html: `<div class="ltm-pin"><svg viewBox="0 0 34 42">
    <path d="M17 2C9.8 2 4 7.8 4 15c0 9.8 13 25 13 25s13-15.2 13-25c0-7.2-5.8-13-13-13z"
      fill="${ACCENT}" stroke="#eafff6" stroke-width="2"/>
    <g transform="translate(9 6.6) scale(.66)" fill="#07150f"><path d="${meta.glyph}"/></g></svg></div>`,
});
const geofenceLabel = (meta) => L.divIcon({
  className: '', iconSize: [0, 0], iconAnchor: [-15, 30],
  html: `<div class="ltm-geolabel"><svg viewBox="0 0 24 24"><path d="${meta.glyph}"/></svg>
    <b>${meta.kind}</b><em>${meta.outer} m</em></div>`,
});
const crewIcon = (type) => L.divIcon({
  className: '', iconSize: [28, 28], iconAnchor: [14, 14],
  html: `<div class="ltm-crewpin"><i>${type || ''}</i></div>`,
});
const dropIcon = () => L.divIcon({
  className: '', iconSize: [28, 34], iconAnchor: [14, 32],
  html: `<div class="ltm-drop"><svg viewBox="0 0 28 34">
    <path d="M14 2C8.5 2 4 6.5 4 12c0 7.4 10 20 10 20s10-12.6 10-20c0-5.5-4.5-10-10-10z"
      fill="#1b232c" stroke="#8e9aa6" stroke-width="2"/>
    <circle cx="14" cy="12" r="3.6" fill="#8e9aa6"/></svg></div>`,
});

// half dial: middle-left → top → middle-right, r = 68 around (86, 74)
const OFF_ROUTE_M = 150;   // beyond this the plotted route is treated as stale
const MOVING_M = 10;       // beyond this since the last fix, count as "Moving" when no speed field
const ARC_LEN = Math.PI * 68;

// Whole-route congestion, from route.durationValue vs route.trafficValue (Directions gives no
// per-segment traffic data — traffic_speed_entry is always empty — so this colors the entire
// drawn route by how much duration_in_traffic exceeds the traffic-free duration.
const TRAFFIC_COLORS = { light: ACCENT, moderate: '#ffb35c', heavy: '#ff5c5c' };
const trafficLevel = (ratio) => (ratio >= 1.8 ? 'heavy' : ratio >= 1.3 ? 'moderate' : 'light');
const dialPoint = (f) => {
  const a = Math.PI * (1 - Math.min(1, Math.max(0, f)));
  return { x: 86 + 68 * Math.cos(a), y: 74 - 68 * Math.sin(a) };
};

export default function LiveTripMap({ fetchers, tracking: trackingProp, className }) {
  const mapEl = useRef(null);
  const map = useRef(null);
  const layers = useRef({ route: [], vehicle: null, trail: null, core: null, drop: null });
  const followRef = useRef(true);
  const spokenStep = useRef(-1);
  const prevFix = useRef(null);

  const [tracking, setTracking] = useState(trackingProp || null);
  const [gps, setGps] = useState(() => gpsFrom(trackingProp));
  const [moving, setMoving] = useState(null); // true/false once derived from position delta; null = unknown
  const [live, setLive] = useState({ remain: null, offRoute: 0 });
  const conn = useRef(null);
  const strikes = useRef(0);
  const heading = useRef(0);
  const [follow, setFollow] = useState(true);
  const [tab, setTab] = useState('crew');
  const [open, setOpen] = useState(false);
  const [voiceOn, setVoiceOn] = useState(true);   // voice guidance on by default
  const [activeStep, setActiveStep] = useState(0);

  const synth = typeof window !== 'undefined' ? window.speechSynthesis : null;

  useEffect(() => {
    if (!trackingProp) return;
    setTracking(trackingProp);
    const g = gpsFrom(trackingProp);
    if (g) setGps((prev) => ({ ...prev, ...g }));
  }, [trackingProp]);
  useEffect(() => { followRef.current = follow; }, [follow]);
  useEffect(() => () => synth?.cancel(), [synth]);

  /* tracking once, then the GPS call on an interval — both supplied by the host */
  useEffect(() => {
    if (!fetchers?.getTracking) return undefined;
    const c = connectTrip({
      ...fetchers,
      onTracking: (p) => {
        setTracking(p);
        const g = gpsFrom(p);
        if (g) setGps((prev) => ({ ...prev, ...g }));
        setLive({ remain: null, offRoute: 0 });
        strikes.current = 0;
      },
      onGps: (p) => {
        const latitude = +p.latitude, longitude = +p.longitude;
        setGps({
          latitude, longitude,
          speed: p.speed != null ? +p.speed : null,
          bearing: p.bearing != null ? +p.bearing : null,
          altitude: p.altitude != null ? +p.altitude : null,
          gpsTime: p.gpsTime || '', address: '',
          isReloadMap: !!p.isReloadMap,
          isGetEta: !!p.isGetEta,
        });
        // No real `speed` reading — infer movement from how far this fix is from the
        // last one, since there's nothing else to go on.
        if (p.speed == null) {
          const prev = prevFix.current;
          setMoving(prev ? metres([prev.latitude, prev.longitude], [latitude, longitude]) > MOVING_M : null);
        } else {
          setMoving(+p.speed > 0);
        }
        prevFix.current = { latitude, longitude };
        // Server-computed (get-vehicle-gps-location compares this fix against the last one
        // cached per trip): true once the vehicle has moved >= 50m. Handled once per poll,
        // right here off the raw payload — not off `gps` state in the vehicle-position effect
        // below, since that effect's own `tracking` dependency would re-fire on every ETA
        // response and keep re-reading the same still-true flag, calling getEta continuously.
        if (p.isReloadMap) c.replot();
        if (p.isGetEta && fetchers?.getEta) {
          fetchers.getEta()
            .then((eta) => {
              if (eta) setTracking((prev) => (prev ? { ...prev, eta: { ...prev.eta, ...eta } } : prev));
            })
            .catch((err) => console.warn('[LiveTripMap] eta refresh failed', err));
        }
      },
      onError: (err) => console.warn('[LiveTripMap] trip poll failed', err),
    });
    conn.current = c;
    return () => { c.stop(); conn.current = null; };
  }, [fetchers]);

  const meCrew = currentPassenger(tracking?.passengers);
  const boarded = !!(tracking?.eta?.isBoarded || meCrew?.isBoarded);
  const route = useMemo(() => routeFromPayload(tracking), [tracking]);
  const geom = useMemo(() => (route ? measure(route.points) : null), [route]);
  const traffic = useMemo(() => {
    if (!route?.durationValue || !route?.trafficValue) return null;
    const ratio = route.trafficValue / route.durationValue;
    return { level: trafficLevel(ratio), ratio, trafficText: route.trafficText, durationText: route.durationText };
  }, [route]);
  /* The dial measures the CURRENT leg: vehicle → pickup, or vehicle → destination
     once boarded. eta.distanceValue sizes it at fetch time; live.remain keeps it
     current from each GPS fix, since the GPS endpoint carries no ETA. */
  const prog = useMemo(() => {
    const e = tracking?.eta || {};
    const total = e.distanceValue || geom?.total || 0;
    const remain = Math.min(total, live.remain != null ? live.remain
      : (e.distanceValue != null ? e.distanceValue : total));
    const legMin = e.durationValue || minutesOf(e.durationText);
    const minutes = Math.max(0, Math.round(legMin * (remain / (total || 1))));
    return {
      total, remain, covered: Math.max(0, total - remain),
      pct: total ? Math.min(100, Math.max(0, Math.round((1 - remain / total) * 100))) : 0,
      minutes,
      message: live.remain == null && e.trackingMessage
        ? e.trackingMessage
        : `${boarded ? 'Drop' : 'Pickup'} in ${minutes < 1 ? '<1' : minutes} min${minutes === 1 ? '' : 's'}`,
    };
  }, [tracking, geom, live.remain, boarded]);
  const geo = gps;

  /* map init */
  useEffect(() => {
    if (map.current || !mapEl.current) return undefined;
    const m = L.map(mapEl.current, { zoomControl: true, attributionControl: true })
      .setView([19.076, 72.877], 12);
    L.tileLayer(TILE, { maxZoom: 19, attribution: '© OpenStreetMap contributors' }).addTo(m);
    m.zoomControl.setPosition('bottomright');
    map.current = m;
    const ro = new ResizeObserver(() => m.invalidateSize());
    ro.observe(mapEl.current);
    return () => { ro.disconnect(); m.remove(); map.current = null; };
  }, []);

  /* route, geofence, crew and drop markers */
  useEffect(() => {
    const m = map.current;
    if (!m || !route || !tracking) return;

    layers.current.route.forEach((l) => m.removeLayer(l));
    layers.current.route = [];
    const add = (l) => { l.addTo(m); layers.current.route.push(l); return l; };

    route.alternates.forEach((a) => add(
      L.polyline(a.points, { color: '#6f7d8c', weight: 3.5, opacity: 0.6, lineCap: 'round' })
        .bindTooltip(`Alternative · ${a.summary} · ${a.distanceText} · ${a.trafficText}`, { sticky: true }),
    ));
    const routeColor = TRAFFIC_COLORS[traffic?.level || 'light'];
    add(L.polyline(route.points, { color: routeColor, weight: 15, opacity: 0.13, lineCap: 'round', interactive: false }));
    layers.current.core = add(L.polyline(route.points, { color: routeColor, weight: 5, opacity: 0.4, lineCap: 'round' }));
    add(L.polyline(route.points, {
      color: '#eafff6', weight: 2.4, opacity: 0.9, className: 'ltm-flow', interactive: false,
    }));
    layers.current.trail = add(L.polyline([], { color: ACCENT, weight: 6, lineCap: 'round', interactive: false }));

    const dest = tracking.destination;
    const nextCrew = currentPassenger(tracking.passengers);
    const targetLL = boarded ? [dest.latitude, dest.longitude] : route.end;
    const gf = boarded
      ? placeMeta(dest, dest.address)
      : placeMeta(null, nextCrew?.pickupLocation || route.endAddress);

    add(L.circle(targetLL, { radius: gf.outer, className: 'ltm-halo', color: ACCENT, weight: 1, fillColor: ACCENT, fillOpacity: 0.1, interactive: false }));
    add(L.circle(targetLL, { radius: gf.inner, className: 'ltm-halo2', color: '#8bf0cd', weight: 1, fillColor: ACCENT, fillOpacity: 0.2, interactive: false }));
    add(L.circle(targetLL, { radius: gf.outer, color: ACCENT, weight: 1.4, opacity: 0.7, dashArray: '4 8', fill: false, interactive: false }));
    add(L.marker(targetLL, { zIndexOffset: 900, icon: targetIcon(gf) }).bindTooltip(
      boarded ? `Drop · ${String(dest.address).slice(0, 90)}` : `Pickup · ${nextCrew?.pickupLocation || route.endAddress}`,
      { direction: 'top', offset: [0, -42] },
    ));
    add(L.marker(targetLL, { interactive: false, zIndexOffset: 890, icon: geofenceLabel(gf) }));

    if (!boarded) {
      (tracking.passengers || []).filter((p) => !p.isBoarded).forEach((p) => add(
        L.marker([p.pickupLatitude, p.pickupLongitude], { zIndexOffset: 880, icon: crewIcon(p.airlineCrewType) })
          .bindTooltip(`${p.crewName} · pickup ${p.pickupTime} · ${p.tripStatus}`, { direction: 'top', offset: [0, -15] }),
      ));
      add(L.marker([dest.latitude, dest.longitude], { icon: dropIcon() })
        .bindTooltip('Final drop', { direction: 'top', offset: [0, -34] }));
    }

    add(L.marker(route.start, {
      icon: L.divIcon({ className: '', iconSize: [20, 20], iconAnchor: [10, 10], html: '<div class="ltm-pin-o"></div>' }),
    }).bindTooltip(`Route start · ${route.startAddress}`, { direction: 'top', offset: [0, -12] }));
  }, [route, tracking, boarded, traffic]);

  /* vehicle position, trail, active step, voice */
  useEffect(() => {
    const m = map.current;
    if (!m || !route || !geom || !geo?.latitude) return;
    const lat = +geo.latitude, lng = +geo.longitude;

    if (!layers.current.vehicle) {
      layers.current.vehicle = L.marker([lat, lng], { zIndexOffset: 1000, icon: vehicleIcon() }).addTo(m);
    } else {
      layers.current.vehicle.setLatLng([lat, lng]);
    }
    const dv = tracking?.driverInfo;
    if (dv) {
      layers.current.vehicle.unbindTooltip().bindTooltip(
        `${dv.vehicleNo} · ${dv.driverName}<br>${geo.address || ''}`,
        { direction: 'top', offset: [0, -17] },
      );
    }

    const { index, offRouteMetres } = snapToRoute(route.points, lat, lng);

    /* Prefer the GPS bearing; at a standstill it is meaningless, so hold the last
       heading, and fall back to the route direction when the field is absent. */
    const isMovingNow = gps?.speed != null && gps.speed > 1;
    const reported = gps?.bearing != null && (isMovingNow || gps.bearing > 0) ? gps.bearing : null;
    const nxt = route.points[Math.min(index + 1, route.points.length - 1)];
    heading.current = reported != null ? reported
      : (isMovingNow ? bearing([lat, lng], nxt) : heading.current);
    const arrow = layers.current.vehicle.getElement()?.querySelector('svg');
    if (arrow) arrow.style.transform = `rotate(${heading.current}deg)`;
    if (layers.current.trail) {
      layers.current.trail.setLatLngs(route.points.slice(0, index + 1).concat([[lat, lng]]));
    }

    setLive({ remain: Math.max(0, geom.total - geom.cum[index]), offRoute: offRouteMetres });

    /* off route for two fixes running → the plotted route is stale, refetch it */
    if (offRouteMetres > OFF_ROUTE_M) {
      strikes.current += 1;
      if (strikes.current >= 2) { strikes.current = 0; if (conn.current) conn.current.replot(); }
    } else strikes.current = 0;

    let si = 0;
    route.steps.forEach((s, n) => {
      if (geom.cum[index] >= geom.cum[snapToRoute(route.points, s.at[0], s.at[1]).index]) si = n;
    });
    if (voiceOn && synth && si !== spokenStep.current) {
      const cur = route.steps[si], after = route.steps[si + 1];
      if (cur && (spokenStep.current !== -1 || si > 0)) {
        const u = new SpeechSynthesisUtterance(
          after ? `${cur.text}. Then in ${cur.distanceText}, ${after.text.toLowerCase()}.`
            : `${cur.text}. Arriving now.`,
        );
        u.lang = 'en-IN';
        synth.speak(u);
      }
      spokenStep.current = si;
    }
    setActiveStep(si);

    if (followRef.current) m.panTo([lat, lng], { animate: true, duration: 0.6 });
  }, [geo?.latitude, geo?.longitude, gps, route, geom, tracking, voiceOn, synth]);

  const fitRoute = () => {
    const m = map.current;
    if (!m || !route) return;
    setFollow(false);
    const b = L.latLngBounds(route.points);
    if (geo?.latitude) b.extend([+geo.latitude, +geo.longitude]);
    m.fitBounds(b, { paddingTopLeft: [290, 40], paddingBottomRight: [235, 50] });
  };
  const flyTo = (ll) => { setFollow(false); if (map.current) map.current.flyTo(ll, 16, { duration: 0.7 }); };

  const dv = tracking?.driverInfo;
  const crew = tracking?.passengers || [];
  const dest = tracking?.destination;
  const boardedCount = crew.filter((p) => p.isBoarded).length;
  const nextCrew = meCrew;
  const destKind = dest
    ? (dest.isResidence ? 'Residence' : dest.isAirport ? 'Airport' : dest.isHotel ? 'Hotel' : 'Ground duty')
    : '';
  const arrival = prog.minutes
    ? new Date(Date.now() + prog.minutes * 60000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '—';
  const knob = dialPoint(prog.pct / 100);

  const summaryText = (() => {
    if (tab === 'crew') {
      return nextCrew
        ? `${nextCrew.isCurrentPassenger ? 'You' : 'Next'}: ${nextCrew.crewName} · ${nextCrew.pickupTime} · ${nextCrew.tripStatus}`
        : `All ${crew.length} crew boarded`;
    }
    if (tab === 'dirs') {
      const s = route?.steps?.[activeStep] || route?.steps?.[0];
      return s ? `${activeStep + 1}. ${s.text} · ${s.distanceText}` : 'No turn list on this route';
    }
    return dest ? `${destKind} · ${String(dest.address).slice(0, 70)}…` : '—';
  })();

  const toggleVoice = () => {
    const next = !voiceOn;
    setVoiceOn(next);
    spokenStep.current = -1;
    if (!next) { if (synth) synth.cancel(); return; }
    const u = new SpeechSynthesisUtterance(
      `Voice guidance on. ${prog.message}${nextCrew ? ` for ${nextCrew.crewName.replace(/\s*\[.*\]/, '')}` : ''}.`,
    );
    u.lang = 'en-IN';
    synth.cancel();
    synth.speak(u);
  };

  return (
    <div className={`ltm ${className || ''}`}>
      <div ref={mapEl} className="ltm-map" />

      <div className="ltm-leftstack">
        {dv && (
          <div className="ltm-driver ltm-glass">
            {dv.transporterName && (
              <div className="ltm-transporter">
                <span className="ltm-transporter-icon">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M4 3h9a1 1 0 0 1 1 1v6h5a1 1 0 0 1 1 1v9h-8v-4H9v4H4V3z" />
                    <rect x="6.5" y="5.5" width="2" height="2" />
                    <rect x="10.5" y="5.5" width="2" height="2" />
                    <rect x="6.5" y="9.5" width="2" height="2" />
                    <rect x="10.5" y="9.5" width="2" height="2" />
                    <rect x="15.5" y="11.5" width="2" height="2" />
                  </svg>
                </span>
                <span className="ltm-transporter-name">{dv.transporterName}</span>
              </div>
            )}
            <div className="ltm-driver-body">
              <div className="ltm-avatar">
                {dv.driverPhotoUrl ? (
                  <img src={String(dv.driverPhotoUrl).replace(/^http:\/\//, 'https://')} alt={dv.driverName}
                    onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                ) : null}
                <span>{dv.driverName.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase()}</span>
              </div>
              <div className="ltm-dinfo">
                <div className="ltm-drow">
                  <div className="ltm-dname">{dv.driverName}</div>
                </div>
                <div className="ltm-dcall">
                  <span className="ltm-dphone ltm-mono">{dv.driverMobileNo}</span>
                  <a className="ltm-call" href={`tel:${String(dv.driverMobileNo).replace(/[^\d+]/g, '')}`}
                    title={`Call ${dv.driverMobileNo}`} aria-label="Call driver">
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M6.6 10.8c1.1 2.2 2.9 4 5.1 5.1l1.7-1.7c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.5.6.6 0 1 .4 1 1V19c0 .6-.4 1-1 1-8.3 0-15-6.7-15-15 0-.6.4-1 1-1h3.4c.6 0 1 .4 1 1 0 1.2.2 2.4.6 3.5.1.3 0 .7-.2 1l-2.1 1.3z" /></svg>
                  </a>
                </div>
                <div className="ltm-dmeta">
                  <span className="ltm-plate ltm-mono">{dv.vehicleNo}</span>
                  <span className="ltm-vtype">{dv.vehicleType}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="ltm-controls ltm-glass">
          <div className="ltm-segs">
            {[['crew', `Crew`], ['dirs', 'Route'], ['drop', boarded ? 'Drop' : 'Pickup']].map(([k, label]) => (
              <button key={k} className="ltm-tab" aria-selected={tab === k}
                onClick={() => { setTab(k); setOpen(true); }}>
                {label}{k === 'crew' && <span className="ltm-count">{boardedCount}/{crew.length}</span>}
              </button>
            ))}
          </div>
          <div className="ltm-acts">
            <button className="ltm-mini" aria-pressed={follow} onClick={() => setFollow((f) => !f)}>Follow</button>
            <button className="ltm-mini" onClick={fitRoute}>Fit route</button>
            <button className="ltm-mini ltm-ic" aria-pressed={voiceOn} disabled={!synth}
              title={synth ? 'Spoken trip updates' : 'No speech synthesis in this browser'}
              onClick={toggleVoice}>
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M4 9v6h3.5L13 20V4L7.5 9H4z" />
                <path className="ltm-wave" d="M16 8.5a4.5 4.5 0 0 1 0 7v-2a2.5 2.5 0 0 0 0-3v-2zm2.6-3a8 8 0 0 1 0 13v-2a6 6 0 0 0 0-9v-2z" />
              </svg>
              <span className="ltm-sr">{voiceOn ? 'Voice on' : 'Voice off'}</span>
            </button>
          </div>
        </div>

        <section className="ltm-details ltm-glass" data-open={open}>
          <button className="ltm-dhead" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
            <span className="ltm-ttl">
              {tab === 'crew' ? `Crew ${boardedCount}/${crew.length}` : tab === 'dirs' ? 'Route' : (boarded ? 'Drop' : 'Pickup')}
            </span>
            <svg className="ltm-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          <div className="ltm-dsum">{summaryText}</div>
          <div className="ltm-dbody">
            {tab === 'crew' && (
              <div className="ltm-pane">
                {crew.map((p) => (
                  <button key={p.employeeNumber}
                    className={`ltm-crew ${p.isBoarded ? 'boarded' : ''} ${p.isCurrentPassenger ? 'me' : ''}`}
                    onClick={() => flyTo([p.pickupLatitude, p.pickupLongitude])}>
                    <span className="ltm-badge">{p.airlineCrewType}</span>
                    <span className="ltm-ctop">
                      <span className="ltm-cname">{p.crewName}</span>
                      {p.isCurrentPassenger && <span className="ltm-you">You</span>}
                      <span className="ltm-status">{p.tripStatus}</span>
                    </span>
                    <span className="ltm-cloc">
                      {p.isBoarded ? `Boarded ${p.boardedTime}`
                        : <>{p.pickupLocation} <em>· {p.pickupTime}</em></>}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {tab === 'dirs' && (
              <div className="ltm-pane">
                {(route?.steps || []).map((s, n) => (
                  <button key={n} className={`ltm-step ${n === activeStep ? 'active' : ''} ${n < activeStep ? 'done' : ''}`}
                    onClick={() => flyTo(s.at)}>
                    <span className="ltm-n ltm-mono">{n + 1}</span>
                    <span className="ltm-t">{s.text}</span>
                    <span className="ltm-d ltm-mono">{s.distanceText}</span>
                  </button>
                ))}
              </div>
            )}

            {tab === 'drop' && dest && (
              <div className="ltm-pane ltm-dropinfo">
                {!boarded && meCrew?.pickupLocation && (
                  <>
                    <b>Pickup</b>{meCrew.pickupLocation}
                    <span className="ltm-tag">{meCrew.pickupTime}</span>
                  </>
                )}
                <b>Final drop</b>{dest.address}
                <span className="ltm-tag">{destKind}</span>
                <span className="ltm-tag ltm-mono">{dest.latitude}, {dest.longitude}</span>
                <span className="ltm-tag">
                  Routing to {boarded ? 'drop' : 'crew pickup'} · via {route?.summary} · {route?.distanceText}
                </span>
              </div>
            )}
          </div>
        </section>
      </div>

      <div className="ltm-card ltm-gauge">
        <div className="ltm-ghead">
          <span className="ltm-lbl">{boarded ? 'Drop in' : 'Pickup in'}</span>
          <span className="ltm-glive"><i />Live</span>
        </div>
        <div className="ltm-gmsg">{prog.message}</div>
        {gps?.speed != null ? (
          <div className="ltm-speed-row">
            <div className="ltm-speed-badge">
              <b className="ltm-speed-value">{Math.round(gps.speed)}</b><span>km/h</span>
            </div>
            <div className={`ltm-status-pill ${gps.speed > 0 ? 'is-moving' : 'is-stopped'}`}>
              {gps.speed > 0 ? 'Moving' : 'Stopped'}
            </div>
          </div>
        ) : moving != null ? (
          <div className="ltm-speed-row">
            <div className={`ltm-status-pill ${moving ? 'is-moving' : 'is-stopped'}`}>
              {moving ? 'Moving' : 'Stopped'}
            </div>
          </div>
        ) : null}
        {traffic && traffic.level !== 'light' && (
          <div className="ltm-gwarn" style={{
            background: traffic.level === 'heavy' ? 'rgba(255,92,92,.14)' : 'rgba(255,179,92,.12)',
            borderColor: traffic.level === 'heavy' ? 'rgba(255,92,92,.4)' : 'rgba(255,179,92,.38)',
            color: TRAFFIC_COLORS[traffic.level],
          }}>
            {traffic.level === 'heavy' ? 'Heavy traffic' : 'Moderate traffic'} on this route
            {' · '}{traffic.trafficText}{traffic.durationText ? ` (usual ${traffic.durationText})` : ''}
          </div>
        )}
        {(live.offRoute > OFF_ROUTE_M || (gps && !gps.gpsTime)) && (
          <div className="ltm-gwarn">
            {live.offRoute > OFF_ROUTE_M
              ? `Vehicle ${live.offRoute} m off the plotted route — refreshing the route…`
              : 'No GPS timestamp reported — position may be stale.'}
          </div>
        )}

        <div className="ltm-dial">
          <svg viewBox="0 0 172 92" aria-hidden="true">
            <path d="M18 74 A 68 68 0 0 1 154 74" fill="none" stroke="rgba(255,255,255,.10)"
              strokeWidth="9" strokeLinecap="round" />
            <path d="M18 74 A 68 68 0 0 1 154 74" fill="none" stroke={ACCENT} strokeWidth="9"
              strokeLinecap="round" strokeDasharray={ARC_LEN}
              strokeDashoffset={ARC_LEN * (1 - prog.pct / 100)}
              style={{ transition: 'stroke-dashoffset .45s ease' }} />
            <g stroke="rgba(255,255,255,.18)" strokeWidth="2.5" strokeLinecap="round">
              <line x1="30" y1="74" x2="52" y2="74" />
              <line x1="120" y1="74" x2="142" y2="74" />
              <line x1="86" y1="6" x2="86" y2="20" />
            </g>
            <circle cx="86" cy="74" r="7" fill="#141a21" stroke="rgba(255,255,255,.22)" strokeWidth="2" />
            <circle cx={knob.x} cy={knob.y} r="4.5" fill="#eafff6"
              style={{ transition: 'cx .45s ease, cy .45s ease' }} />
          </svg>
          <div className="ltm-dialtext">
            <b className="ltm-mono">{km(prog.remain)}</b>
            <span>remaining</span>
          </div>
        </div>

        <div className="ltm-gstats">
          <div><div className="ltm-gk">Covered</div><div className="ltm-gv ltm-mono">{km(prog.covered)}</div></div>
          <div className="ltm-rule" />
          <div><div className="ltm-gk">Progress</div><div className="ltm-gv ltm-mono">{prog.pct}%</div></div>
          <div className="ltm-rule ltm-rule2" />
          <div className="ltm-third"><div className="ltm-gk">Arrival</div><div className="ltm-gv ltm-mono">{arrival}</div></div>
        </div>

        <div className="ltm-gfoot">
          {tracking?.eta?.routeDistanceText || km(geom?.total || 0)} whole trip
          {' · via '}{route?.summary}
          {' · GPS '}{gps?.gpsTime || 'time not reported'}
          {gps?.altitude ? ` · ${Math.round(gps.altitude)} m alt` : ''}
        </div>
      </div>
    </div>
  );
}
